<template>
  <template v-if="$route.name === 'announcements.index'">
    <div class="d-flex justify-content-end mb-12 m-8 align-items-center" 
         style="direction: rtl; margin-top: 1rem; margin-bottom: 4rem;">
      <button v-can="'announcements.create'" class="btn btn-secondary btn-sm" @click="navigateToAdd">
        <i class="fas fa-plus ms-2"></i>
        {{ $t('announcement.add') }}
      </button>
    </div>

    <div class="col-12 col-xl-12">
      <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 1rem; overflow: visible">
        <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
          <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
                {{ $t('announcement.manage') }}
              </h6>
            </div>
          </div>
        </div>

        <div class="card-body px-0 pb-2 pe-xl-3">
          <LoadingSpinner v-if="loading" :message="$t('announcement.loading')" class="py-5" />

          <div v-else class="announcement-carousel-container">
            <div v-if="announcements.length === 0" class="text-center py-5 text-muted">
              <i class="fas fa-bullhorn fa-3x mb-3"></i>
              <p>{{ $t('announcement.noActive') }}</p>
              <button v-can="'announcements.create'" class="btn btn-primary mt-3" @click="navigateToAdd">
                <i class="fas fa-plus ms-2"></i>
                {{ $t('announcement.addNew') }}
              </button>
            </div>

            <div v-else class="carousel-wrapper" dir="rtl">
              <div class="carousel-slides" :style="{ 
                transform: `translateX(${currentIndex * 100}%)`,
                direction: 'rtl',
                transition: 'transform 0.5s ease-in-out'
              }">
                <div v-for="(announcement) in announcements" :key="announcement.id" class="carousel-slide">
                  <div class="announcement-card">
                    <!-- Status Badge -->
                    <span class="badge announcement-badge" :class="announcement.status === 'active' ? 'bg-success' : 'bg-warning'">
                      {{ announcement.status === 'active' ? $t('announcement.active') : $t('announcement.inactive') }}
                    </span>
                    
                    <!-- Audience Scope Badge -->
                    <div class="announcement-scope">
                      <span class="badge bg-info audience-badge">
                        <i class="fas fa-users me-1"></i>
                        {{ getAudienceDisplay(announcement) }}
                      </span>
                    </div>

                    <!-- Date -->
                    <span class="announcement-date">
                      <i class="fas fa-calendar-alt ms-2"></i>
                      {{ formatDate(announcement.date) }}
                    </span>

                    <!-- Image -->
                    <img v-if="announcement.image" 
                         :src="announcement.image" 
                         :alt="announcement.title" 
                         class="announcement-image" 
                         @error="handleImageError" />
                    <div v-else class="announcement-placeholder">
                      <i class="fas fa-bullhorn fa-4x"></i>
                    </div>

                    <!-- Overlay Content -->
                    <div class="announcement-overlay">
                      <div class="announcement-content">
                        <h5 class="announcement-title">{{ announcement.title }}</h5>
                        <p class="announcement-description">{{ announcement.description }}</p>

                        <div class="announcement-actions">
                          <button v-can="'announcements.update'" class="btn btn-secondary" @click.stop="navigateToEdit(announcement.id)">
                            <i class="fas fa-edit"></i> {{ $t('announcement.edit') }}
                          </button>
                          <button v-can="'announcements.delete'" class="btn btn-danger" @click.stop="openDeleteDialog(announcement)">
                            <i class="fas fa-trash"></i> {{ $t('announcement.delete') }}
                          </button>
                        </div>

                        <div class="carousel-indicators">
                          <button v-for="(_, idx) in announcements" 
                                  :key="idx" 
                                  :class="{ 'active': currentIndex === idx }" 
                                  @click="goToSlide(idx)"></button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button class="carousel-control prev" @click="prevSlide">
                <i class="fas fa-chevron-right"></i>
              </button>
              <button class="carousel-control next" @click="nextSlide">
                <i class="fas fa-chevron-left"></i>
              </button>
            </div>
          </div>

          <AnnouncementDeleteDialog ref="deleteDialog" @delete="deleteAnnouncement" />
        </div>
      </div>
    </div>
  </template>

  <router-view></router-view>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import AnnouncementDeleteDialog from '@/components/common/DeleteDialog.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';

const { t, locale } = useI18n();
const router = useRouter();
const toast = useToast();
const store = useStore();
const deleteDialog = ref(null);
const currentIndex = ref(0);

const loading = computed(() => store.getters['announcements/isLoading']);
const announcements = computed(() => {
  const data = store.getters['announcements/announcements'];
  return Array.isArray(data) ? data : [];
});

// Audience display helper
const getAudienceDisplay = (announcement) => {
  const audienceType = announcement.audience_type;
     const groupClassName = announcement.class_name || announcement.class?.name || `Class #${announcement.class_id}`;
      const groupName = announcement.group_name || announcement.class_group?.group?.name || `Group #${announcement.class_group_id}`;
          const className = announcement.class_name || announcement.class?.name || `Class #${announcement.class_id}`;

  switch (audienceType) {
    case 'all':
      return t('announcement.scopeAll');
    
    case 'class':
      return ` ${className}`;
    
    case 'group':
    return ` ${groupClassName} |  ${groupName}`;
    
    default:
      return t('announcement.scopeAll');
  }
};

// Carousel controls
const nextSlide = () => {
  if (!announcements.value.length) return;
  currentIndex.value = (currentIndex.value + 1) % announcements.value.length;
};

const prevSlide = () => {
  if (!announcements.value.length) return;
  currentIndex.value = currentIndex.value === 0 ? announcements.value.length - 1 : currentIndex.value - 1;
};

const goToSlide = (index) => {
  if (!announcements.value.length) return;
  currentIndex.value = index;
};

// Date formatting
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(locale.value || 'ar', options);
  } catch (error) {
    console.error("Date formatting error:", error);
    return dateString;
  }
};

// Image error handling
const handleImageError = (e) => {
  e.target.src = '/images/default-announcement.png';
};

// Error handling utilities
function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object') {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t('auth.unauthorized', 'Unauthorized'));
  if (s === 403) return toast.error(t('auth.forbidden', 'Forbidden'));
  return toast.error(extractApiMessage(error, fallbackKey));
}

// Data operations
const fetchAnnouncements = async () => {
  try {
    await store.dispatch('announcements/fetchAnnouncements');
  } catch (error) {
    notifyError(error, 'announcement.loadError');
    console.error('fetchAnnouncements error:', error);
  }
};

const openDeleteDialog = (announcement) => {
  deleteDialog.value?.openDialog(announcement.id, announcement.title);
};

const deleteAnnouncement = async (id) => {
  try {
    await store.dispatch('announcements/deleteAnnouncement', id);
    toast.success(t('announcement.deleteSuccess'));
    await fetchAnnouncements();
    if (currentIndex.value >= announcements.value.length) {
      currentIndex.value = Math.max(0, announcements.value.length - 1);
    }
  } catch (error) {
    notifyError(error, 'announcement.deleteError');
  }
};

// Navigation
const navigateToAdd = () => {
  router.push({ name: 'announcements.create' });
};

const navigateToEdit = (id) => {
  router.push({ name: 'announcements.edit', params: { id } });
};

// Auto-advance carousel
let interval;
onMounted(async () => {
  await fetchAnnouncements();
  if (announcements.value.length > 1) {
    interval = setInterval(nextSlide, 5000);
  }
});

onUnmounted(() => clearInterval(interval));
</script>

<style scoped>
.announcement-carousel-container {
  position: relative;
  overflow: hidden;
  border-radius: 0.5rem;
  margin: 1rem 0;
  direction: rtl;
}

.carousel-wrapper {
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;
}

.carousel-slides {
  display: flex;
  height: 100%;
  width: 100%;
}

.carousel-slide {
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  position: relative;
}

.announcement-card {
  position: relative;
  height: 100%;
  width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.announcement-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.announcement-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  color: #6c757d;
}

.announcement-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.announcement-content {
  max-width: 800px;
  width: 100%;
  color: white;
  text-align: center;
  padding: 2rem;
}

.announcement-title {
  font-size: 1.8rem;
  font-weight: bold;
  color: white;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.announcement-description {
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}

.announcement-badge {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  font-size: 0.8rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.announcement-date {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
  font-size: 0.9rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
}

.announcement-scope {
  position: absolute;
  bottom: 20px;
  left: 20px;
  z-index: 10;
}

.audience-badge {
  font-size: 0.8rem;
  padding: 0.5rem 0.75rem;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}


.announcement-actions {


  gap: 2rem;
  margin: 2rem;
}

.announcement-actions .btn {
  min-width: 100px;
  margin: 0.5rem;
  gap: 4rem;;
}
.carousel-control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.3);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s;
}

.carousel-control:hover {
  background: rgba(255, 255, 255, 0.5);
  transform: translateY(-50%) scale(1.1);
}

.carousel-control.prev {
  right: 1rem;
}

.carousel-control.next {
  left: 1rem;
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.carousel-indicators button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0;
  transition: all 0.3s;
}

.carousel-indicators button:hover {
  transform: scale(1.2);
}

.carousel-indicators button.active {
  background: white;
  transform: scale(1.2);
}

@media (max-width: 768px) {
  .carousel-wrapper {
    height: 450px;
  }
  
  .announcement-title {
    font-size: 1.5rem;
  }
  
  .announcement-description {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
  
  .announcement-content {
    padding: 1.5rem;
  }
  
  .audience-badge {
    max-width: 200px;
    font-size: 0.7rem;
  }
}

@media (max-width: 576px) {
  .carousel-wrapper {
    height: 400px;
  }
  
  .announcement-title {
    font-size: 1.3rem;
  }
  
  .announcement-description {
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
  
  .announcement-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .carousel-control {
    width: 30px;
    height: 30px;
  }
  
  .announcement-badge,
  .announcement-date,
  .audience-badge {
    font-size: 0.7rem;
    padding: 0.3rem 0.5rem;
  }
  
  .audience-badge {
    max-width: 150px;
  }
}
</style>